import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import AlertsBell from "./AlertsBell";
import Logo from "./Logo";
import IndexStrip from "./IndexStrip";
import PwaInstallButton from "./PwaInstallButton";
import { api } from "../lib/api";
import { TrendUp, SignOut, UserCircle, ArrowsClockwise, CircleNotch, Sun, Moon, Crown, EnvelopeSimple, ChatCircleDots, Calculator, Question } from "@phosphor-icons/react";
import { toast } from "sonner";

export default function TopBar({ onRunScan, scanning, lastScan }) {
  const nav = useNavigate();
  const { user, logout, refresh } = useAuth();
  const { theme, toggle } = useTheme();
  const [isPremium, setIsPremium] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);

  const toggleEmailAlerts = async () => {
    if (emailBusy) return;
    setEmailBusy(true);
    try {
      const enabled = !user?.email_alerts;
      const { data } = await api.post("/settings/email-alerts", { enabled });
      if (refresh) await refresh();
      if (enabled) {
        const res = data?.test_email;
        if (res?.skipped) {
          toast.warning("Email alerts enabled — but sending is not configured yet (admin setup needed).");
        } else if (res?.ok) {
          toast.success(`Email alerts ON. Test email sent to ${user.email}`);
        } else {
          toast.success("Email alerts ON.");
        }
      } else {
        toast("Email alerts turned off.");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally {
      setEmailBusy(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    api
      .get("/subscription/me")
      .then(({ data }) => {
        if (!cancelled) setIsPremium(!!data.is_premium);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);
  const ls = lastScan?.completed_at
    ? new Date(lastScan.completed_at).toLocaleString("en-IN", {
        dateStyle: "medium",
        timeStyle: "short",
      })
    : "Never";
  return (
    <header
      className="sticky top-0 z-20 bg-app/80 backdrop-blur-md border-b border-subtle"
      data-testid="top-bar"
    >
      <div className="flex items-center justify-between px-6 py-4">
        <div className="flex items-center gap-6">
          <div className="flex items-center gap-3">
            <Logo size={40} />
            <div className="flex items-baseline gap-1.5 font-mono text-sm tracking-[0.22em] uppercase">
              <span className="text-primary-fg font-semibold">Breakout</span>
              <span className="text-pos hidden sm:inline font-semibold">Scanner</span>
            </div>
          </div>
          <div className="hidden md:flex items-center gap-4 text-[10px] font-mono uppercase tracking-widest text-muted-fg">
            <span>
              Last Scan: <span className="text-primary-fg">{ls}</span>
            </span>
            {lastScan?.signals_found != null && (
              <span>
                · <span className="text-primary-fg">{lastScan.signals_found}</span> signals
              </span>
            )}
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => nav("/referral")}
            data-testid="refer-earn-button"
            className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-mod-soft bg-mod-soft text-mod text-[10px] font-mono uppercase tracking-widest hover:opacity-90 transition"
            title="Refer & Earn — 50 credits = ₹50"
          >
            🎁 Refer & Earn
          </button>
          {isPremium ? (
            <button
              type="button"
              onClick={() => nav("/pricing")}
              data-testid="premium-badge"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-pos-soft bg-pos-soft text-pos text-[10px] font-mono uppercase tracking-widest"
            >
              <Crown size={12} weight="fill" /> Premium
            </button>
          ) : (
            <button
              type="button"
              onClick={() => nav("/pricing")}
              data-testid="upgrade-button"
              className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-sm text-[10px] font-mono uppercase tracking-widest transition"
              style={{ background: "hsl(var(--signal-positive))", color: "#fff" }}
            >
              <Crown size={12} weight="fill" /> Upgrade
            </button>
          )}
          <PwaInstallButton />
          <button
            type="button"
            onClick={onRunScan}
            disabled={scanning}
            data-testid="run-scan-button"
            title="Scans NIFTY 500 universe (~500 stocks, ~3 min). Full NSE universe scan runs automatically every day at 4 PM IST."
            className="px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-widest hover:opacity-90 transition-all disabled:opacity-70 flex items-center gap-2"
            style={{ background: "hsl(var(--text-primary))", color: "hsl(var(--bg))" }}
          >
            {scanning ? (
              <>
                <CircleNotch size={14} className="animate-spin" weight="bold" />
                Scanning
              </>
            ) : (
              <>
                <ArrowsClockwise size={14} weight="bold" />
                Run Scan
              </>
            )}
          </button>

          <div className="flex items-center gap-2 px-3 py-1.5 border border-subtle rounded-sm">
            {user?.picture ? (
              <img src={user.picture} alt="" className="w-5 h-5 rounded-full" />
            ) : (
              <UserCircle size={16} className="text-muted-fg" />
            )}
            <span className="text-xs text-secondary-fg font-mono hidden sm:inline">
              {user?.email}
            </span>
          </div>
          <AlertsBell />
          <button
            type="button"
            onClick={toggleEmailAlerts}
            disabled={emailBusy}
            data-testid="email-alerts-toggle"
            title={user?.email_alerts ? "Email alerts ON — click to disable" : "Turn on email alerts for Target / SL"}
            className={
              "p-2 border rounded-sm hover:bg-surface-hover transition " +
              (user?.email_alerts
                ? "border-pos-soft bg-pos-soft text-pos"
                : "border-subtle text-muted-fg hover:text-primary-fg")
            }
          >
            {emailBusy ? <CircleNotch size={14} className="animate-spin" /> : <EnvelopeSimple size={14} weight={user?.email_alerts ? "fill" : "regular"} />}
          </button>
          {user?.role === "admin" && (
            <button
              type="button"
              onClick={() => nav("/admin")}
              data-testid="admin-link"
              title="Admin panel"
              className="px-2.5 py-1.5 border border-subtle rounded-sm hover:bg-surface-hover text-secondary-fg hover:text-primary-fg transition text-[10px] font-mono uppercase tracking-widest"
            >
              Admin
            </button>
          )}
          <button
            type="button"
            onClick={() => nav("/risk-calculator")}
            data-testid="risk-calc-link"
            title="Risk / Reward calculator"
            className="p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-pos transition"
          >
            <Calculator size={14} weight="regular" />
          </button>
          <button
            type="button"
            onClick={() => {
              // Re-arm the onboarding tour. Dashboard listens to this storage event.
              localStorage.removeItem("bs.onboarding.v2");
              localStorage.setItem("bs.tour.replay", String(Date.now()));
              // Make sure we're on the dashboard so the tour can find its targets
              if (window.location.pathname !== "/dashboard") {
                nav("/dashboard");
              } else {
                // Trigger storage event manually for same-tab listeners
                window.dispatchEvent(new StorageEvent("storage", { key: "bs.tour.replay" }));
              }
            }}
            data-testid="tour-link"
            title="Take a guided tour"
            className="p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-pos transition"
          >
            <Question size={14} weight="regular" />
          </button>
          <button
            type="button"
            onClick={() => nav("/feedback")}
            data-testid="feedback-link"
            title="Send feedback or suggestion"
            className="p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-pos transition"
          >
            <ChatCircleDots size={14} weight="regular" />
          </button>
          <button
            type="button"
            onClick={toggle}
            data-testid="theme-toggle"
            title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
            className="p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-primary-fg transition"
          >
            {theme === "dark" ? <Sun size={14} weight="bold" /> : <Moon size={14} weight="bold" />}
          </button>
          <button
            type="button"
            onClick={logout}
            data-testid="logout-button"
            title="Sign out"
            className="p-2 border border-subtle rounded-sm hover:bg-surface-hover text-muted-fg hover:text-primary-fg transition"
          >
            <SignOut size={14} />
          </button>
        </div>
      </div>
      <IndexStrip compact />
    </header>
  );
}
